import createGlobalState from "@ui/lib/createGlobalState";
import * as io from "io-ts";
import { literalUnion, nullable } from "diagnostics-lib/io";
import t from "../../../i18n";
import SidePanelLayout from "@ui/SidePanel/Layout";
import React from "react";
import Modal from "@ui/Modal";
import useModal from "@ui/lib/hooks/useModal";
import Button from "@ui/Button";
import CreateUserModalForm from "./CreateUserModalForm";
import Section from "@ui/Section";
import TableCard from "@ui/Table/Card";
import Table from "./Table";
import type { UserProfil } from "../../../api/useUsers";
import { useUsers } from "../../../api/useUsers";
import type { User } from "../../../api/useUser";
import { useUser } from "../../../api/useUser";
import { useIndexedProjects } from "../../../api/useProjects";
import useFilteredDatas from "@ui/lib/hooks/useFilteredDatas";
import type { FilterOption } from "@ui/Filter/FilterSelect";
import FilterSelects from "@ui/Filter";
import { canUserDoOnSomeProject } from "diagnostics-domain/canUserDo";
import SearchInputV2 from "@ui/Input/SearchInputV2";
import { AddCircleLine } from "@ui/IconV2/react";
import { getGlobalRoleName } from "./globalRoleOptions";

const useUserSearch = createGlobalState({
  storage: "session",
  key: "userSearch",
  type: io.string,
  defaultValue: "",
  scoped: false,
});

export const GLOBAL_ROLES = [
  "SUPER_ADMIN",
  "ADMIN",
  "ALL_PROJECTS_READER",
  "ALL_PROJECTS_READER_WITH_JUPYTER",
  "ALL_PROJECTS_SETUPER",
  "ALL_PROJECTS_WRITER",
  "PROD_READER",
  "DIAGNOSER",
] as const;
const globalRole = literalUnion(GLOBAL_ROLES);
export type GlobalRole = io.TypeOf<typeof globalRole>;

const FilteredValuesByFilterType = io.strict({
  role: io.array(nullable(globalRole)),
  project: io.array(nullable(io.string)),
});
type FilteredValuesByFilterType = io.TypeOf<typeof FilteredValuesByFilterType>;

const DEFAULT_FILTER_VALUES: FilteredValuesByFilterType = {
  project: [],
  role: [],
};

const useFilteredValuesByFilterType = createGlobalState({
  storage: "session",
  key: "usersTableFilteredValues",
  type: FilteredValuesByFilterType,
  defaultValue: DEFAULT_FILTER_VALUES,
  scoped: false,
});

const UsersActions = ({ user }: { user: User }) => {
  const hasUserCreatePermission = canUserDoOnSomeProject(user, "USER_MANAGE");
  const { modal, closeModal, openModal } = useModal();

  if (!hasUserCreatePermission) return null;
  return (
    <>
      <Modal title={t("users.modal.create.add_user")} isOpen={modal} onRequestClose={closeModal}>
        <CreateUserModalForm onRequestClose={closeModal} />
      </Modal>
      <Button leftIcon={<AddCircleLine />} variant="primary" onClick={openModal}>
        {t("users.modal.create.add_user")}
      </Button>
    </>
  );
};

const Users = () => {
  const user = useUser();
  const usersRaws = useUsers();

  const projects = useIndexedProjects();
  const [search, setSearch] = useUserSearch({});
  const [filteredValues, setFilteredValues] = useFilteredValuesByFilterType({});

  const handleSearchChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.currentTarget.value);
    },
    [setSearch],
  );

  const userFilterMetadata = React.useMemo(
    () => ({
      role: {
        title: t("users.selectFilter.role"),
        getOptions: (user: UserProfil) =>
          user.roles.global
            ? {
                label: getGlobalRoleName(user.roles.global),
                value: user.roles.global as GlobalRole | null,
              }
            : null,
        withNone: true,
      },
      project: {
        title: t("users.selectFilter.project"),
        getOptions: (user: UserProfil) =>
          user.roles.projects.reduce<FilterOption<string | null>[]>((acc, role) => {
            const project = projects[role.projectId];
            if (project) {
              acc.push({ label: project.name, value: project.id.toString() });
            }
            return acc;
          }, []),
        withNone: true,
      },
    }),
    [projects],
  );

  const filteredUsers = React.useMemo(
    () =>
      usersRaws.filter(user => {
        const userRole = user.roles.global;

        const filteredRoles = filteredValues.role;
        const filteredProjects = filteredValues.project;
        const userProjectsId = user.roles.projects.map(p => p.projectId);

        const noFilter = filteredRoles.length === 0 && filteredProjects.length === 0;
        const matchRoleFilter = filteredRoles.includes(userRole) && filteredProjects.length === 0;

        const matchRoleWithNoProjectFilter =
          filteredRoles.includes(userRole) &&
          filteredProjects.includes(null) &&
          user.roles.projects.length === 0;

        const matchProjectFilter =
          (filteredRoles.length === 0 &&
            filteredProjects.length > 0 &&
            filteredProjects.some(id => id && userProjectsId.includes(id))) ||
          (filteredRoles.length === 0 &&
            filteredProjects.includes(null) &&
            user.roles.projects.length === 0);

        const matchProjectAndRoleFilter =
          filteredProjects.length > 0 &&
          userProjectsId.length > 0 &&
          filteredRoles.includes(userRole) &&
          filteredProjects.some(id => id && userProjectsId.includes(id));

        return (
          noFilter ||
          matchProjectFilter ||
          matchRoleFilter ||
          matchRoleWithNoProjectFilter ||
          matchProjectAndRoleFilter
        );
      }),
    [filteredValues.role, usersRaws, filteredValues.project],
  );

  const filterDatas = React.useCallback((u: UserProfil) => `${u.login} ${u.name}`, []);
  const [filteredDatas, dataLoading] = useFilteredDatas(filteredUsers, search, filterDatas);

  // Removed deleted project ids from all users' projectRoles..
  const users = React.useMemo(
    () =>
      filteredDatas.map(rawUser => ({
        ...rawUser,
        projectRoles: rawUser.roles.projects.filter(pr => projects[pr.projectId] !== undefined),
      })),
    [filteredDatas, projects],
  );

  return (
    <SidePanelLayout>
      <Section id="main" title={t("users.title")} actions={<UsersActions user={user} />}>
        <TableCard
          title={null}
          headerLeft={
            <>
              <SearchInputV2
                placeholder={t("commons.placeholder.search")}
                value={search}
                onChange={handleSearchChange}
              />
              <FilterSelects
                data={usersRaws}
                metadata={userFilterMetadata}
                filterValues={filteredValues}
                setFilterValues={setFilteredValues}
                defaultFilterValues={DEFAULT_FILTER_VALUES}
              />
            </>
          }
          headerRight={null}
        >
          <Table loading={dataLoading} users={users} />
        </TableCard>
      </Section>
    </SidePanelLayout>
  );
};

export default Users;
